import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText } from 'lucide-react';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/" className="inline-flex items-center text-accent-primary hover:text-accent-primary/80 transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center space-x-4">
            <div className="bg-accent-primary/10 p-3 rounded-full">
              <Shield className="w-8 h-8 text-accent-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-dark">Privacy Policy</h1>
              <p className="text-subtle">How we protect and handle your personal information</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        
        {/* Introduction */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Eye className="w-5 h-5 mr-2 text-accent-primary" />
              Privacy Notice
            </CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <p>
              This privacy policy notice is served by <strong>All About Hair Extensions</strong> under the websites 
              www.all-about-hair-extensions.com, www.hairextensionsfranchise.com, www.hairextensiontraining.academy. 
              The purpose of this policy is to explain to you how we control, process, handle and protect your personal 
              information through the business and while you browse or use this website.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
              <p className="text-yellow-800 font-medium">
                If you do not agree to the following policy you may wish to cease viewing / using this website, 
                and or refrain from submitting your personal data to us.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Key Definitions */}
        <Card>
          <CardHeader>
            <CardTitle>Policy Key Definitions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <strong>"I", "our", "us", or "we"</strong> refer to the business, All About Hair Extensions – Hair Extensions Training Academy and Need a Hair Makeover
              </div>
              <div>
                <strong>"you", "the user"</strong> refer to the person(s) using this website.
              </div>
              <div>
                <strong>GDPR</strong> means General Data Protection Act.
              </div>
              <div>
                <strong>PECR</strong> means Privacy & Electronic Communications Regulation.
              </div>
              <div>
                <strong>ICO</strong> means Information Commissioner's Office.
              </div>
              <div>
                <strong>Cookies</strong> mean small files stored on a users computer or device.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* GDPR Principles */}
        <Card>
          <CardHeader>
            <CardTitle>Key Principles of GDPR</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Our privacy policy embodies the following key principles:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Lawfulness, fairness and transparency</li>
              <li>Purpose limitation</li>
              <li>Data minimisation</li>
              <li>Accuracy</li>
              <li>Storage limitation</li>
              <li>Integrity and confidence</li>
              <li>Accountability</li>
            </ul>
          </CardContent>
        </Card>

        {/* Processing Personal Data */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Lock className="w-5 h-5 mr-2 text-accent-primary" />
              Processing of Your Personal Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              Under the GDPR (General Data Protection Regulation) we control and / or process any personal 
              information about you electronically using the following lawful bases.
            </p>
            <div className="bg-accent-primary/5 border border-accent-primary/20 rounded-lg p-4 mb-4">
              <p className="font-semibold text-accent-primary">
                We are registered with the ICO under the Data Protection Register, our registration number is: ZA345269
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-lg mb-2">Lawful basis: Contract</h4>
                <p><strong>Purpose:</strong> Deliver your order or services</p>
                <p><strong>Processing methods:</strong> Collection of Name, address, email and telephone to deliver your order.</p>
                <p><strong>Data retention:</strong> We will continue to process your information under this basis until you withdraw consent or it is determined your consent no longer exists.</p>
                <p><strong>Sharing:</strong> We do not share your information with third parties without explicit consent.</p>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">
                If, as determined by us, the lawful basis upon which we process your personal information changes, 
                we will notify you about the change and any new lawful basis to be used if required.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Individual Rights */}
        <Card>
          <CardHeader>
            <CardTitle>Your Individual Rights</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Under the GDPR your rights are as follows:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>The right to be informed</li>
              <li>The right of access</li>
              <li>The right to rectification</li>
              <li>The right to erasure</li>
              <li>The right to restrict processing</li>
              <li>The right to data portability</li>
              <li>The right to object</li>
              <li>The right not to be subject to automated decision-making including profiling</li>
            </ul>
            <div className="mt-4 p-4 bg-gray-100 rounded-lg">
              <p>
                You also have the right to complain to the ICO (<a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">www.ico.org.uk</a>) 
                if you feel there is a problem with the way we are handling your data.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cookies */}
        <Card>
          <CardHeader>
            <CardTitle>Internet Cookies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              We use cookies on this website to provide you with a better user experience. We do this by placing 
              a small text file on your device / computer hard drive to track how you use the website, to record 
              or log whether you have seen particular messages that we display, to keep you logged into the website 
              where applicable, to display relevant adverts or content.
            </p>
            
            <div>
              <h4 className="font-semibold mb-2">Types of data we collect:</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Browser type</li>
                <li>Operating System</li>
                <li>Mobile unit information</li>
                <li>IP address</li>
                <li>Pages you visit</li>
                <li>Time spent on pages</li>
                <li>Last visit</li>
                <li>References (where you come from)</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Third-party cookies:</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Analyzing visitor movement and behavior to improve site design</li>
                <li>Building segments for advertising based on visitor behavior</li>
                <li>Serving relevant ads to previous visitors</li>
                <li>Testing website content and structure</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Social Media Cookies:</h4>
              <p>
                We use Facebook cookies to allow you to "like" and share our pages, and Twitter cookies to "tweet" our pages. 
                These platforms create their own cookies to track engagement.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Data Security */}
        <Card>
          <CardHeader>
            <CardTitle>Data Security and Protection</CardTitle>
          </CardHeader>
          <CardContent>
            <p>
              We ensure the security of any personal information we hold by using secure data storage technologies 
              and precise procedures in how we store, access and manage that information. Our methods meet the 
              GDPR compliance requirement.
            </p>
          </CardContent>
        </Card>

        {/* Email Marketing */}
        <Card>
          <CardHeader>
            <CardTitle>Email Marketing Messages & Subscription</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              Under the GDPR we use the consent lawful basis for anyone subscribing to our newsletter or marketing 
              mailing list. Any email marketing messages we send are done so through an EMS (Email Marketing Service) provider.
            </p>
            
            <div>
              <h4 className="font-semibold mb-2">Our EMS provider is Wix. We hold the following information:</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Email address</li>
                <li>IP address</li>
                <li>Subscription time & date</li>
              </ul>
            </div>

            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">
                We provide you with an easy method to withdraw your consent (unsubscribe) or manage your 
                preferences at any time through any marketing message.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Resources */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="w-5 h-5 mr-2 text-accent-primary" />
              Resources & Further Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <li><a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">Overview of the GDPR - General Data Protection Regulation</a></li>
              <li><a href="https://www.gov.uk/data-protection" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">Data Protection Act 1998</a></li>
              <li><a href="https://www.facebook.com/help/cookies" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">Facebook Privacy Policy</a></li>
              <li><a href="https://twitter.com/privacy" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">Twitter Privacy Policy</a></li>
              <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">Google Privacy Policy</a></li>
            </ul>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Us</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">If you have any questions about this privacy policy, please contact us:</p>
            <div className="space-y-2">
              <p>📞 <a href="tel:+442030514338" className="text-accent-primary hover:underline">+44 20 3051 4338</a></p>
              <p>📧 <a href="mailto:needahairmakeover@gmail.com" className="text-accent-primary hover:underline">needahairmakeover@gmail.com</a></p>
              <p>🌐 <a href="https://www.needahairmakeoverfranchise.co.uk" target="_blank" rel="noopener noreferrer" className="text-accent-primary hover:underline">www.needahairmakeoverfranchise.co.uk</a></p>
            </div>
          </CardContent>
        </Card>

        {/* Last Updated */}
        <Card>
          <CardContent className="text-center text-sm text-gray-500">
            <p>This privacy policy was last updated: {new Date().toLocaleDateString()}</p>
            <p className="mt-2">© 2026 Need A Hair Makeover Franchise. All rights reserved.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};